import styled from 'styled-components';

import colors from 'styles/theme/colors';

interface Props extends React.HTMLAttributes<HTMLSpanElement> {
  color?: 'green' | 'red';
  // Opt-in only — undefined for every existing caller, so a plain Badge/FilledBadge with no
  // icon renders exactly as before.
  icon?: React.ReactNode;
}

export default function Badge(props: Props) {
  const { color, icon, children, ...rest } = props;

  return (
    <ColoredBadge data-cy='badge' color={color} {...rest}>
      {icon}
      {children}
    </ColoredBadge>
  );
}

function getColors(color: Props['color'] = 'green') {
  const colorMap = {
    green: {
      backgroundColor: colors.green10,
      color: colors.green100,
    },
    red: {
      backgroundColor: colors.red10,
      color: colors.red100,
    },
  };

  return colorMap[color];
}

const ColoredBadge = styled.span<{ color: Props['color'] }>`
  padding: 2px 12px;
  box-sizing: border-box;
  border-radius: 24px;
  color: ${({ color }) => getColors(color).color};
  background-color: ${({ color }) => getColors(color).backgroundColor};
  font-size: 10px;
`;

// The decision-chip look used for a status that's been settled (Accepted/Denied on the facility
// decisions step, Selected/Not Selected on the referral outcome step) — a darker text tone on the
// shared Badge's tint than the default (Hover, not 100), plus tighter padding and a heavier
// weight. Shared so the two stay visually identical rather than drifting apart as two local copies.
export const FilledBadge = styled(Badge)`
  background-color: ${({ color }) => (color === 'red' ? colors.red10 : colors.green10)};
  color: ${({ color }) => (color === 'red' ? colors.redHover : colors.greenHover)};
  padding: 2px 8px;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;
